import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { registerToastHandler } from '../services/toastBridge';

export const ToastContext = createContext(null);

let _id = 0;

const SuccessIcon = () => (
  <svg style={{ width: 20, height: 20, flexShrink: 0, marginTop: 1 }} viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);
const ErrorIcon = () => (
  <svg style={{ width: 20, height: 20, flexShrink: 0, marginTop: 1 }} viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);
const WarningIcon = () => (
  <svg style={{ width: 20, height: 20, flexShrink: 0, marginTop: 1 }} viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const InfoIcon = () => (
  <svg style={{ width: 20, height: 20, flexShrink: 0, marginTop: 1 }} viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ICONS = { success: <SuccessIcon />, error: <ErrorIcon />, warning: <WarningIcon />, info: <InfoIcon /> };

const ToastItem = ({ toast, onClose }) => (
  <div className={`toast toast-${toast.type}`} role="alert">
    {ICONS[toast.type] || ICONS.info}
    <div className="toast-body">
      {toast.title && <div className="toast-title">{toast.title}</div>}
      <div className="toast-message">{toast.message}</div>
    </div>
    <button className="toast-close" onClick={onClose} aria-label="Dismiss">&times;</button>
  </div>
);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback(({ type = 'info', message, title, duration = 4000 }) => {
    const id = ++_id;
    setToasts(prev => [...prev, { id, type, message, title }]);
    if (duration > 0) setTimeout(() => removeToast(id), duration);
    return id;
  }, [removeToast]);

  // Lets non-React code (axios interceptors) trigger a toast, e.g. on request timeout.
  useEffect(() => {
    registerToastHandler(addToast);
    return () => registerToastHandler(null);
  }, [addToast]);

  // Back-compat: many call sites do `const { showToast } = useContext(ToastContext)`
  // then `showToast(message, type)` — a different shape than addToast({type, message}).
  // Kept as a thin adapter rather than rewriting every call site.
  const showToast = useCallback((message, type = 'info') => addToast({ type, message }), [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, showToast }}>
      {children}
      <div className="toast-container" aria-live="polite">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onClose={() => removeToast(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
